import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Menu, Moon, Sun, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/equalaccess-logo.png";
import flagEN from "@/assets/flag-en.png";
import flagNL from "@/assets/flag-nl.png";

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const knowledgeLinks = [
    { key: 'nav.knowledge.digital', url: '/kennisbank/digitale-toegankelijkheid' },
    { key: 'nav.knowledge.disabilities', url: '/kennisbank/soorten-beperkingen' },
    { key: 'nav.knowledge.wcag', url: '/kennisbank/wcag' },
    { key: 'nav.knowledge.wdo', url: '/kennisbank/wet-digitale-overheid' },
  ];
   
  return (
    <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-7xl" aria-label="Hoofdnavigatie">
      <div className="bg-[hsl(208_66%_12%)] dark:bg-[hsl(208_66%_8%)] backdrop-blur-sm rounded-full shadow-lg border border-white/10 px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 gap-2">
          <Link to="/" className="flex items-center gap-2 lg:gap-3 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] rounded-md p-1 min-h-[44px] flex-shrink-0" aria-label="EqualAccess homepage">
            <img src={logo} alt="" className="h-8 w-8 lg:h-10 lg:w-10 flex-shrink-0 object-contain" aria-hidden="true" />
            <span className="text-base lg:text-xl font-bold text-white whitespace-nowrap">EqualAccess</span>
          </Link>
          
          {/* Desktop Navigation - shows at ~850px for better zoom compliance */}
          <div className="hidden min-[850px]:flex items-center gap-2 2xl:gap-4 flex-shrink min-w-0">
            <Link 
              to="/services" 
              className="text-white/90 hover:text-white transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] rounded px-2 2xl:px-3 py-2.5 min-h-[44px] inline-flex items-center whitespace-nowrap"
            >
              {t('nav.services')}
            </Link>
            <Link 
              to="/approach" 
              className="text-white/90 hover:text-white transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] rounded px-2 2xl:px-3 py-2.5 min-h-[44px] inline-flex items-center whitespace-nowrap"
            >
              {t('nav.approach')}
            </Link>

            {/* Knowledge Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-white/90 hover:text-white transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] rounded px-2 2xl:px-3 py-2.5 min-h-[44px] inline-flex items-center gap-1 whitespace-nowrap">
                {t('nav.knowledge')}
                <ChevronDown className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {knowledgeLinks.map((link) => (
                  <DropdownMenuItem key={link.url} asChild>
                    <Link to={link.url} className="w-full cursor-pointer">
                      {t(link.key)}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/about" 
              className="text-white/90 hover:text-white transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] rounded px-2 2xl:px-3 py-2.5 min-h-[44px] inline-flex items-center whitespace-nowrap"
            >
              {t('nav.about')}
            </Link>
            
            <button
              onClick={() => setLanguage(language === 'nl' ? 'en' : 'nl')}
              className="flex items-center gap-1 2xl:gap-2 px-2 2xl:px-3 py-2.5 min-h-[44px] min-w-[44px] rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] flex-shrink-0"
              aria-label={language === 'nl' ? 'Schakel naar Engels' : 'Switch to Dutch'}
            >
              <img 
                src={language === 'nl' ? flagEN : flagNL} 
                alt="" 
                className="w-5 h-3.5 object-cover rounded-sm flex-shrink-0"
                aria-hidden="true"
              />
              <span className="text-xs font-semibold uppercase">
                {language === 'nl' ? 'EN' : 'NL'}
              </span>
            </button>

            <button
              onClick={() => setTheme((resolvedTheme ?? 'light') === 'dark' ? 'light' : 'dark')}
              className="flex items-center gap-2 px-2 2xl:px-3 py-2.5 min-h-[44px] min-w-[44px] rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] flex-shrink-0"
              aria-label={(resolvedTheme ?? 'light') === 'dark' ? 'Schakel naar lichte modus' : 'Schakel naar donkere modus'}
              aria-pressed={(resolvedTheme ?? 'light') === 'dark'}
              disabled={!mounted}
            >
              {mounted && ((resolvedTheme ?? 'light') === 'dark' ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />)}
              {!mounted && <Moon className="h-5 w-5" aria-hidden="true" />}
            </button>
            
            
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center bg-white text-[hsl(208_66%_12%)] hover:bg-white/90 font-semibold text-sm px-4 2xl:px-6 py-2.5 min-h-[44px] rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[hsl(208_66%_12%)] transition-colors whitespace-nowrap flex-shrink-0"
            >
              {t('nav.contact')}
            </Link>
          </div>

          {/* Mobile Navigation - shows below ~850px */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="min-[850px]:hidden">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/10 min-h-[44px] min-w-[44px] focus:ring-white focus:ring-offset-[hsl(208_66%_12%)] flex-shrink-0" 
                aria-label={isOpen ? "Sluit menu" : "Open menu"}
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
              >
                <Menu className="h-6 w-6" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto" id="mobile-menu">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-6 mt-8 pb-8" aria-label="Mobiel menu">
                <Link 
                  to="/services"
                  onClick={() => setIsOpen(false)}
                  className="text-lg text-foreground hover:text-primary transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-3 py-3 min-h-[44px] inline-flex items-center"
                >
                  {t('nav.services')}
                </Link>
                <Link 
                  to="/approach"
                  onClick={() => setIsOpen(false)}
                  className="text-lg text-foreground hover:text-primary transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-3 py-3 min-h-[44px] inline-flex items-center"
                >
                  {t('nav.approach')}
                </Link>

                {/* Knowledge Base Mobile Accordion */}
                <div className="px-3">
                  <button
                    onClick={() => setKnowledgeOpen(!knowledgeOpen)}
                    className="w-full text-lg text-foreground hover:text-primary transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded py-3 min-h-[44px] inline-flex items-center justify-between"
                    aria-expanded={knowledgeOpen}
                  >
                    {t('nav.knowledge')}
                    <ChevronDown className={`h-5 w-5 transition-transform ${knowledgeOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  {knowledgeOpen && (
                    <div className="pl-4 space-y-2 mt-2">
                      {knowledgeLinks.map((link) => (
                        <Link
                          key={link.url}
                          to={link.url}
                          onClick={() => setIsOpen(false)}
                          className="block text-base text-muted-foreground hover:text-primary transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                        >
                          {t(link.key)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link 
                  to="/about"
                  onClick={() => setIsOpen(false)}
                  className="text-lg text-foreground hover:text-primary transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-3 py-3 min-h-[44px] inline-flex items-center"
                >
                  {t('nav.about')}
                </Link>
                
                <div className="border-t border-border pt-6 space-y-4">
                  <button
                    onClick={() => setLanguage(language === 'nl' ? 'en' : 'nl')}
                    className="flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-md text-foreground hover:text-primary hover:bg-secondary/50 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 w-full"
                    aria-label={language === 'nl' ? 'Schakel naar Engels' : 'Switch to Dutch'}
                  >
                    <img 
                      src={language === 'nl' ? flagEN : flagNL} 
                      alt="" 
                      className="w-6 h-4 object-cover rounded-sm"
                      aria-hidden="true"
                    />
                    <span className="text-lg font-semibold uppercase">
                      {language === 'nl' ? 'EN' : 'NL'}
                    </span>
                  </button>

                  <button
                    onClick={() => setTheme((resolvedTheme ?? 'light') === 'dark' ? 'light' : 'dark')}
                    className="flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-md text-foreground hover:text-primary hover:bg-secondary/50 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 w-full"
                    aria-label={(resolvedTheme ?? 'light') === 'dark' ? 'Schakel naar lichte modus' : 'Schakel naar donkere modus'}
                    aria-pressed={(resolvedTheme ?? 'light') === 'dark'}
                    disabled={!mounted}
                  >
                    {mounted && ((resolvedTheme ?? 'light') === 'dark' ? <Sun className="h-6 w-6" aria-hidden="true" /> : <Moon className="h-6 w-6" aria-hidden="true" />)}
                    {!mounted && <Moon className="h-6 w-6" aria-hidden="true" />}
                    <span className="text-lg font-medium">
                      {mounted ? ((resolvedTheme ?? 'light') === 'dark' ? 'Lichte modus' : 'Donkere modus') : 'Donkere modus'}
                    </span>
                  </button>
                </div>
                
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center w-full bg-accent hover:bg-accent/90 text-accent-foreground min-h-[44px] rounded-md px-4 py-3 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 mt-4" 
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.contact')}
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
